import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import {
  usePracticeEntities,
  useCreatePracticeEntity,
  useUpdatePracticeEntity,
  type PracticeEntity,
  type CreatePracticeEntityPayload,
} from './hooks/usePracticeEntities';

/**
 * PracticeEntitiesPage — List and manage practice entities with create/edit modals.
 * Supports CRUD operations with branding configuration.
 *
 * Validates: Requirements 2.1, 2.8
 */
export function PracticeEntitiesPage() {
  const { data: entities, isLoading } = usePracticeEntities();
  const createMutation = useCreatePracticeEntity();
  const updateMutation = useUpdatePracticeEntity();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<PracticeEntity | null>(null);

  const handleCreate = useCallback(() => {
    setEditingEntity(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((entity: PracticeEntity) => {
    setEditingEntity(entity);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingEntity(null);
  }, []);

  const handleSubmit = useCallback(
    async (data: CreatePracticeEntityPayload) => {
      if (editingEntity) {
        await updateMutation.mutateAsync({ id: editingEntity.id, ...data });
      } else {
        await createMutation.mutateAsync(data);
      }
      handleCloseModal();
    },
    [editingEntity, createMutation, updateMutation, handleCloseModal],
  );

  const handleToggleActive = useCallback(
    async (entity: PracticeEntity) => {
      await updateMutation.mutateAsync({
        id: entity.id,
        isActive: !entity.isActive,
      });
    },
    [updateMutation],
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Practice Entities</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage offices, legal entities, and brands within your firm
          </p>
        </div>
        <Button onClick={handleCreate}>Add Entity</Button>
      </div>

      {/* Entities List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"
            role="status"
          >
            <span className="sr-only">Loading practice entities...</span>
          </div>
        </div>
      ) : !entities || entities.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No practice entities configured yet. Create your first entity to get started.
            </p>
            <Button className="mt-4" onClick={handleCreate}>
              Create Practice Entity
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {entities.map((entity) => (
            <Card key={entity.id} className="relative">
              <div className="space-y-3">
                {/* Entity branding color indicator */}
                {entity.branding?.primaryColor && (
                  <div
                    className="h-2 w-full rounded-t"
                    style={{ backgroundColor: entity.branding.primaryColor }}
                    aria-hidden="true"
                  />
                )}

                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {entity.name}
                    </h3>
                    {entity.code && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Code: {entity.code}
                      </p>
                    )}
                  </div>
                  <Badge variant={entity.isActive ? 'success' : 'default'} size="sm">
                    {entity.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {entity.email && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">{entity.email}</p>
                )}

                {entity.phone && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">{entity.phone}</p>
                )}

                {entity.address?.city && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {[entity.address.city, entity.address.province].filter(Boolean).join(', ')}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-border dark:border-border-dark">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(entity)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(entity)}
                    loading={updateMutation.isPending}
                  >
                    {entity.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <PracticeEntityModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        entity={editingEntity}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}

// ─── Modal Form ─────────────────────────────────────────────────────────────

interface PracticeEntityModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePracticeEntityPayload) => Promise<void>;
  entity: PracticeEntity | null;
  isLoading: boolean;
}

function PracticeEntityModal({
  open,
  onClose,
  onSubmit,
  entity,
  isLoading,
}: PracticeEntityModalProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [defaultTaxRate, setDefaultTaxRate] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens
  const resetForm = useCallback(() => {
    if (entity) {
      setName(entity.name);
      setCode(entity.code ?? '');
      setBusinessNumber(entity.businessNumber ?? '');
      setPhone(entity.phone ?? '');
      setEmail(entity.email ?? '');
      setWebsite(entity.website ?? '');
      setCity(entity.address?.city ?? '');
      setProvince(entity.address?.province ?? '');
      setPostalCode(entity.address?.postalCode ?? '');
      setPrimaryColor(entity.branding?.primaryColor ?? '');
      setSecondaryColor(entity.branding?.secondaryColor ?? '');
      setInvoicePrefix(entity.invoicePrefix ?? '');
      setDefaultTaxRate(entity.defaultTaxRate?.toString() ?? '');
      setPaymentTermsDays(entity.paymentTermsDays?.toString() ?? '');
    } else {
      setName('');
      setCode('');
      setBusinessNumber('');
      setPhone('');
      setEmail('');
      setWebsite('');
      setCity('');
      setProvince('');
      setPostalCode('');
      setPrimaryColor('');
      setSecondaryColor('');
      setInvoicePrefix('');
      setDefaultTaxRate('');
      setPaymentTermsDays('');
    }
    setError('');
  }, [entity]);

  // Reset on open/entity change
  useState(() => {
    resetForm();
  });

  // Use effect equivalent for modal open state
  if (open && name === '' && entity?.name) {
    resetForm();
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const payload: CreatePracticeEntityPayload = {
      name: name.trim(),
      code: code.trim() || undefined,
      businessNumber: businessNumber.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      website: website.trim() || undefined,
      invoicePrefix: invoicePrefix.trim() || undefined,
      defaultTaxRate: defaultTaxRate ? parseFloat(defaultTaxRate) : undefined,
      paymentTermsDays: paymentTermsDays ? parseInt(paymentTermsDays, 10) : undefined,
    };

    if (city || province || postalCode) {
      payload.address = {
        city: city.trim() || undefined,
        province: province.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        country: 'Canada',
      };
    }

    if (primaryColor || secondaryColor) {
      payload.branding = {
        primaryColor: primaryColor.trim() || undefined,
        secondaryColor: secondaryColor.trim() || undefined,
      };
    }

    try {
      await onSubmit(payload);
      resetForm();
    } catch {
      setError('Failed to save practice entity. Please try again.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title={entity ? 'Edit Practice Entity' : 'Create Practice Entity'}
      description="Configure office, legal entity, or brand details."
      size="lg"
    >
      <form onSubmit={handleFormSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700" role="alert">
            {error}
          </div>
        )}

        {/* Basic Information */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Entity Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Toronto Office"
          />
          <Input
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g., TO"
            hint="Short identifier for the entity"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Business Number"
            value={businessNumber}
            onChange={(e) => setBusinessNumber(e.target.value)}
            placeholder="e.g., 123456789"
          />
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g., +1 416 555 0123"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="office@firm.com"
          />
          <Input
            label="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://firm.com"
          />
        </div>

        {/* Address */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Toronto"
          />
          <Input
            label="Province"
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            placeholder="Ontario"
          />
          <Input
            label="Postal Code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="M5V 1A1"
          />
        </div>

        {/* Branding */}
        <fieldset className="rounded-lg border border-border p-4 dark:border-border-dark">
          <legend className="px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Branding
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Primary Color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#3B82F6"
              hint="Hex color code"
            />
            <Input
              label="Secondary Color"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              placeholder="#10B981"
              hint="Hex color code"
            />
          </div>
        </fieldset>

        {/* Billing Configuration */}
        <fieldset className="rounded-lg border border-border p-4 dark:border-border-dark">
          <legend className="px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Billing
          </legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Invoice Prefix"
              value={invoicePrefix}
              onChange={(e) => setInvoicePrefix(e.target.value)}
              placeholder="INV-TO"
            />
            <Input
              label="Default Tax Rate (%)"
              value={defaultTaxRate}
              onChange={(e) => setDefaultTaxRate(e.target.value)}
              type="number"
              placeholder="13"
            />
            <Input
              label="Payment Terms (days)"
              value={paymentTermsDays}
              onChange={(e) => setPaymentTermsDays(e.target.value)}
              type="number"
              placeholder="30"
            />
          </div>
        </fieldset>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border dark:border-border-dark">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {entity ? 'Save Changes' : 'Create Entity'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
